import { Modal, Button, Alert, Tag } from '../../ui';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr0 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * Shown when "Place Order" is clicked (payers only), BEFORE the order is created. Driven by the cart's
 * advance-CD preview (`est`, fetched by the parent):
 *  • eligible → shows the discount benefit + "Place & pay ₹net" (and "Place without advance");
 *  • assigned to a scheme but below the tier → shows the eligibility criteria + a plain "Place order".
 * The order is created ONLY on an explicit choice — closing this dialog (X / Cancel) creates nothing.
 * When no scheme is assigned the parent skips this dialog entirely and places directly.
 */
export function AdvancePayPromptModal({ est, summary, placing, onCancel, onPlace, onPayAndPlace }: {
  est: any | null;                 // advance-CD preview for the cart (null = closed)
  summary?: { subtotal?: number; gstPct?: number | null } | null; // cart gross + GST rate, for the full breakdown
  placing: boolean;                // an order create is in flight
  onCancel: () => void;            // dismiss — creates nothing
  onPlace: () => void;             // place without an advance
  onPayAndPlace: () => void;       // place, then collect the advance (eligible only)
}) {
  const open = !!est;
  const eligible = !!est?.eligible;
  const basisLabel = est?.basisLabel === 'grand' ? 'grand total' : 'basic (taxable)';

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onCancel(); }}
      title={eligible ? 'Place order — pay an advance?' : 'Place order'}
      width={560}
    >
      {!est ? null : eligible ? (
        <>
          <p style={{ marginTop: 0 }}>If you pay before billing, you earn an extra cash discount. Here's the full breakdown:</p>
          {(() => {
            // Bridge the cart's standing/dealer discount into the CD waterfall so it reads end-to-end.
            const gross = Number(summary?.subtotal ?? est.original.taxable);          // items before ANY discount
            const dealerDisc = Math.max(0, gross - Number(est.original.taxable));      // 5% general/dealer discount (reconciles to taxable)
            const dealerPct = gross > 0 ? Math.round((dealerDisc / gross) * 100) : 0;
            const gstNow = Number(est.net.cgst) + Number(est.net.sgst) + Number(est.net.igst);
            const gstPct = summary?.gstPct != null ? summary.gstPct
              : (Number(est.net.taxable) > 0 ? Math.round((gstNow / Number(est.net.taxable)) * 100) : null);
            const roundNow = Number(est.netPayable) - (Number(est.net.taxable) + gstNow); // whole-rupee round-off, so rows sum exactly
            return (
              <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', fontSize: 14 }}>
                <Row label="Items (before discount)" value={inr2(gross)} />
                {dealerDisc >= 0.005 && <Row label={`Dealer discount${dealerPct > 0 ? ` (${dealerPct}%)` : ''}`} value={`− ${inr2(dealerDisc)}`} accent="#1F8A4C" />}
                <Row label="Taxable value" value={inr2(est.original.taxable)} />
                <Row label={`Cash discount (${Number(est.pct)}%)`} value={`− ${inr2(est.cdAmount)}`} accent="#1F8A4C" />
                <Row label="Taxable after cash discount" value={inr2(est.net.taxable)} />
                <Row label={`GST${gstPct != null ? ` (${gstPct}%)` : ''}`} value={`+ ${inr2(gstNow)}`} />
                {Math.abs(roundNow) >= 0.005 && <Row label="Round off" value={`${roundNow >= 0 ? '+ ' : '− '}${inr2(Math.abs(roundNow))}`} />}
                <Row label="Pay now" value={<b>{inr2(est.netPayable)}</b>} strong />
              </div>
            );
          })()}
          <Alert
            type="success" style={{ marginTop: 12 }}
            title={`You save ${inr2(est.totalBenefit)} by paying now — ${inr2(est.netPayable)} today vs ${inr2(est.original.grand)} at billing.`}
          />
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>{est.note}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button variant="default" onClick={onCancel} disabled={placing}>Cancel</Button>
            <Button variant="default" onClick={onPlace} loading={placing}>Place without advance</Button>
            <Button variant="primary" onClick={onPayAndPlace} loading={placing}>Place &amp; pay {inr2(est.netPayable)}</Button>
          </div>
        </>
      ) : (
        <>
          <Alert
            type="info" style={{ marginBottom: 12 }}
            title="This order doesn't qualify for a before-billing cash discount yet"
            description={
              est?.nextTier
                ? <>Pay <b>{inr2(est.nextTier.gap)}</b> more before billing (order {basisLabel} value ≥ <b>{inr2(est.nextTier.minAmount)}</b>) to unlock a <b>{Number(est.nextTier.cdPct)}%</b> cash discount.</>
                : <>Your dealer scheme offers a before-billing cash discount on larger orders — see the tiers below.</>
            }
          />
          {Array.isArray(est?.tiers) && est.tiers.length > 0 && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#fafafa', fontSize: 12, color: '#8c8c8c' }}>
                <span>Pay before billing ({basisLabel})</span><span>Cash discount</span>
              </div>
              {est.tiers.map((t: any, i: number) => {
                const reached = Number(est.basisValue) >= Number(t.minAmount);
                return (
                  <Row
                    key={i}
                    label={<span>{inr2(t.minAmount)}+ {reached && <Tag tone="green" style={{ marginLeft: 6 }}>reached</Tag>}</span>}
                    value={`${Number(t.cdPct)}% off`}
                    accent={reached ? '#1F8A4C' : undefined}
                  />
                );
              })}
              <Row label={<span style={{ color: '#8c8c8c' }}>Your order's {basisLabel} value</span>} value={inr2(est.basisValue)} strong />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button variant="default" onClick={onCancel} disabled={placing}>Cancel</Button>
            <Button variant="primary" onClick={onPlace} loading={placing}>Place order · {inr2(est?.original?.grand)}</Button>
          </div>
        </>
      )}
    </Modal>
  );
}

function Row({ label, value, accent, strong }: { label: any; value: any; accent?: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #f5f5f5', background: strong ? '#fafafa' : undefined }}>
      <span style={{ color: '#595959' }}>{label}</span>
      <span style={{ color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
